import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import { PoweredByMarquee } from "@/components/PoweredByMarquee";
import { WaitlistDialog } from "@/components/WaitlistDialog";

const CIRCUIT_SVG = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='320' height='320' viewBox='0 0 320 320' fill='none' stroke='%23e8721a' stroke-width='1.5'>
    <path d='M0 40 L120 40 L140 60 L240 60 L260 80 L320 80'/>
    <path d='M0 120 L80 120 L100 140 L200 140 L220 120 L320 120'/>
    <path d='M0 200 L60 200 L80 220 L180 220 L200 200 L280 200 L300 220 L320 220'/>
    <path d='M0 280 L100 280 L120 260 L220 260 L240 280 L320 280'/>
    <path d='M40 0 L40 80 L60 100 L60 200 L80 220'/>
    <path d='M140 0 L140 60'/>
    <path d='M200 140 L200 200'/>
    <path d='M260 80 L260 180 L280 200'/>
    <path d='M120 260 L120 320'/>
    <path d='M220 260 L220 320'/>
    <circle cx='40' cy='40' r='3' fill='%23e8721a'/>
    <circle cx='140' cy='60' r='3' fill='%23e8721a'/>
    <circle cx='260' cy='80' r='3' fill='%23e8721a'/>
    <circle cx='100' cy='140' r='3' fill='%23e8721a'/>
    <circle cx='200' cy='140' r='3' fill='%23e8721a'/>
    <circle cx='80' cy='220' r='3' fill='%23e8721a'/>
    <circle cx='200' cy='200' r='3' fill='%23e8721a'/>
    <circle cx='280' cy='200' r='3' fill='%23e8721a'/>
    <circle cx='120' cy='260' r='3' fill='%23e8721a'/>
    <circle cx='220' cy='260' r='3' fill='%23e8721a'/>
  </svg>`
)}`;

export function Hero() {
  const { t } = useI18n();
  const cyclingWords = t("hero.cycling").split("|");
  const [wordIdx, setWordIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    setWordIdx(0);
  }, [cyclingWords.length]);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setWordIdx(i => (i + 1) % cyclingWords.length);
        setVisible(true);
      }, 350);
    }, 2200);
    return () => clearInterval(timer);
  }, [cyclingWords.length]);

  return (
    <section id="hero" className="relative overflow-hidden pt-20 pb-32 scroll-mt-20" style={{ backgroundColor: "#0a1845" }}>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url("${CIRCUIT_SVG}")`,
          backgroundSize: "320px 320px",
          backgroundRepeat: "repeat",
          opacity: 0.28,
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at 50% 30%, rgba(232,114,26,0.10), transparent 60%)" }}
      />
      <div className="absolute -bottom-32 -left-32 w-[32rem] h-[32rem] rounded-full bg-primary/20 blur-[140px] pointer-events-none" />
      <div className="absolute top-1/4 -right-32 w-96 h-96 rounded-full bg-primary/10 blur-[120px] pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-border bg-card/50 text-xs font-medium text-muted-foreground mb-8">
          <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          {t("hero.badge")}
        </div>

        <h1
          className="text-6xl md:text-8xl font-bold tracking-tight leading-[1.0] mb-6"
          style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}
        >
          <span style={{ color: "#ffffff" }}>{t("hero.static")} </span>
          <span style={{ display: "inline-block", minWidth: "4.8em", textAlign: "left" }}>
            <span
              style={{
                display: "inline-block",
                color: "#f97316",
                transition: "opacity 0.35s ease, transform 0.35s ease",
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(-10px)",
              }}
            >
              {cyclingWords[wordIdx]}
            </span>
          </span>
          <br />
          <span style={{
            background: "linear-gradient(90deg, #f97316 0%, #dc2626 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}>
            {t("hero.allWithAi")}
          </span>
        </h1>

        <p className="text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed" style={{ color: "#94a3b8" }}>
          {t("hero.subtitle")}
        </p>

        <div className="flex items-center justify-center mb-8">
          <WaitlistDialog>
            <button type="button" className="btn-spin-wrap">
              <span className="btn-spin-inner inline-flex items-center justify-center px-10 py-3.5 text-base font-semibold text-white hover:brightness-110 transition">
                {t("hero.cta.start")}
              </span>
            </button>
          </WaitlistDialog>
        </div>

        <p className="text-xs text-muted-foreground mb-16">
          {t("hero.waitlistNote")}
        </p>

        <PoweredByMarquee label={t("hero.poweredBy")} />
      </div>
    </section>
  );
}
